// ServiceOS/frontend/src/components/forms/PublicReviewFormPage.js

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import FormRenderer from './FormRenderer'; // Assuming FormRenderer is in the same directory
import api from '../../utils/api'; // Adjust path as needed
import Loader from '../common/Loader'; // Adjust path as needed
import { toast } from 'react-toastify'; // For notifications

const PublicReviewFormPage = () => {
    const { formId } = useParams(); // Get formId from URL path
    const location = useLocation(); // Get location object to parse query params
    const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

    // Extract jobId and customerId from query parameters
    const jobId = queryParams.get('jobId');
    const customerId = queryParams.get('customerId');

    const [formDefinition, setFormDefinition] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [submissionSuccess, setSubmissionSuccess] = useState(false);

    useEffect(() => {
        const fetchForm = async () => {
            setIsLoading(true);
            setError(null);
            try {
                // Fetch the public form definition using its ID
                const res = await api.get(`/public/forms/${formId}`);
                setFormDefinition(res.data);
            } catch (err) {
                console.error("Failed to fetch public form:", err);
                setError("Failed to load review form. It might not be active or does not exist.");
            } finally {
                setIsLoading(false);
            }
        };

        if (formId) {
            fetchForm();
        } else {
            setError("No form ID provided in the URL.");
            setIsLoading(false);
        }
    }, [formId]);

    // ⭐ MODIFIED: Updated to accept and submit FormData directly.
    const handleSubmitReview = async (formDataFromRenderer) => {
        setIsLoading(true);
        setError(null);
        setSubmissionSuccess(false);

        if (!jobId || !customerId) {
            setError("Missing job or customer information in the URL. Cannot submit review.");
            setIsLoading(false);
            return;
        }

        // The FormRenderer already included purpose, jobId, and customerId in the FormData,
        // but we ensure jobId/customerId are explicitly checked here before submission.

        try {
            // Submit the FormData object. We must ensure the API client does not manually set 
            // the 'Content-Type' header so the browser can set the 'multipart/form-data' boundary correctly.
            const res = await api.post(`/public/forms/${formId}/submit`, formDataFromRenderer, {
                // Remove Content-Type header to allow browser to calculate boundary
                headers: {
                    'Content-Type': undefined, 
                }
            });
            toast.success(res.data.message || "Your review has been submitted!");
            setSubmissionSuccess(true);
        } catch (err) {
            console.error("Error submitting review form:", err);
            setError(err.response?.data?.message || "Failed to submit your review. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen flex flex-col justify-center items-center bg-slate-50 dark:bg-slate-950">
                <Loader />
                <p className="mt-4 text-slate-500 dark:text-slate-400 font-medium animate-pulse">Loading review form...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
                <div className="w-full max-w-md bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl rounded-3xl shadow-2xl p-8 text-center border border-red-100 dark:border-red-900/50">
                    <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 text-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
                        <span className="text-3xl font-bold">!</span>
                    </div>
                    <p className="font-black text-2xl text-slate-900 dark:text-white mb-2 tracking-tight">Oops</p>
                    <p className="text-slate-500 dark:text-slate-400 font-medium">{error}</p>
                </div>
            </div>
        );
    }

    // ⭐ UPGRADED: Sleek Success UI preventing the basic box look
    if (submissionSuccess) {
        return (
            <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
                <div className="w-full max-w-md bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl rounded-3xl shadow-2xl p-8 sm:p-10 text-center animate-in zoom-in-95 fade-in duration-500 border border-slate-200 dark:border-slate-800">
                    <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 text-green-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
                        <span className="text-4xl">🎉</span>
                    </div>
                    <h2 className="text-3xl font-black text-slate-900 dark:text-white mb-4 tracking-tight">Thank You!</h2>
                    <p className="text-slate-500 dark:text-slate-400 text-lg font-medium">
                        Your review has been successfully submitted. We appreciate your feedback!
                    </p>
                    {/* ⭐ NEW: Review forms invite every reviewer to also leave a public review */}
                    {(formDefinition?.reviewLinks?.googleReviewLink || formDefinition?.reviewLinks?.trustpilotLink) && (
                        <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-800">
                            <p className="text-slate-700 dark:text-slate-300 font-semibold mb-4">
                                Could you also share your experience publicly? It really helps us.
                            </p>
                            <div className="flex flex-col gap-3">
                                {formDefinition.reviewLinks.googleReviewLink && (
                                    <a
                                        href={formDefinition.reviewLinks.googleReviewLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl bg-blue-600 hover:bg-blue-700 text-white font-bold shadow-lg shadow-blue-500/25 transition-colors"
                                    >
                                        Leave a Google review
                                    </a>
                                )}
                                {formDefinition.reviewLinks.trustpilotLink && (
                                    <a
                                        href={formDefinition.reviewLinks.trustpilotLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl bg-[#00B67A] hover:bg-[#00a36d] text-white font-bold shadow-lg transition-colors"
                                    >
                                        Review us on Trustpilot
                                    </a>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    if (!formDefinition) {
        // This case might happen if formId is missing initially or after submissionSuccess
        return (
            <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
                <div className="w-full max-w-md bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl rounded-3xl shadow-2xl p-8 text-center border border-slate-200 dark:border-slate-800">
                    <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 text-slate-400 rounded-full flex items-center justify-center mx-auto mb-6">
                        <span className="text-2xl font-bold">?</span>
                    </div>
                    <p className="font-black text-2xl text-slate-900 dark:text-white mb-2 tracking-tight">Form Not Available</p>
                    <p className="text-slate-500 dark:text-slate-400 font-medium">The review form could not be loaded or has already been submitted. Please check the link.</p>
                </div>
            </div>
        );
    }

    // ⭐ AESTHETIC UPGRADE: Moved to slate background and stripped the hard white box so FormRenderer handles the glass container
    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col items-center justify-center p-4 sm:p-8">
            <div className="w-full max-w-3xl animate-in fade-in slide-in-from-bottom-4 duration-500">
                <h1 className="text-4xl font-black text-slate-900 dark:text-white mb-8 text-center tracking-tight drop-shadow-sm">Leave Your Review</h1>
                <FormRenderer
                    formDefinition={formDefinition}
                    onSubmit={handleSubmitReview}
                    submitButtonText="Submit Review"
                    isLoading={isLoading}
                    jobId={jobId} // Pass jobId to FormRenderer (though FormRenderer embeds it in FormData)
                    customerId={customerId} // Pass customerId to FormRenderer
                />
            </div>
        </div>
    );
};

export default PublicReviewFormPage;